import { ChevronDown, ChevronRight, Wrench } from "lucide-react";
import { useId, useState } from "react";

import { cn } from "@/lib/utils";

/**
 * Collapsible disclosure for technical-only fields - user_id, cognito
 * sub, resource UUIDs, KMS ARNs, correlation ids, source IP, etc.
 *
 * Root admins occasionally need these values (debugging, audit search,
 * filing support tickets), but they shouldn't squat prominently in
 * every card for every user by default. Wrap those fields in this
 * component and they render behind a "Technical details" toggle.
 *
 * Two usage modes - the types enforce that you pick ONE, via a
 * discriminated union on the `open` prop:
 *
 *   Uncontrolled (default):
 *     <TechnicalDetails>…</TechnicalDetails>
 *     <TechnicalDetails defaultOpen>…</TechnicalDetails>
 *
 *   Controlled (parent owns the open state, e.g. syncing two
 *   disclosures or persisting across row remounts):
 *     <TechnicalDetails open={open} onToggle={setOpen}>…</TechnicalDetails>
 *
 * The `aria-controls` wiring points the toggle button at a stable id
 * on the revealed panel so screen readers announce the controlled
 * region correctly.
 */

interface BaseProps {
  children: React.ReactNode;
  /** Custom toggle label. Defaults to "Technical details". */
  label?: string;
  /** Extra classes for the outer wrapper. */
  className?: string;
}

interface UncontrolledProps extends BaseProps {
  open?: undefined;
  onToggle?: undefined;
  /** Initial open state in uncontrolled mode. Default false. */
  defaultOpen?: boolean;
}

interface ControlledProps extends BaseProps {
  /** Controlled open state. */
  open: boolean;
  /** Required when `open` is provided - the parent owns state updates. */
  onToggle: (open: boolean) => void;
  defaultOpen?: undefined;
}

export type TechnicalDetailsProps = UncontrolledProps | ControlledProps;

export function TechnicalDetails(props: TechnicalDetailsProps): JSX.Element {
  const [localOpen, setLocalOpen] = useState(
    isControlled(props) ? false : (props.defaultOpen ?? false),
  );
  const open = isControlled(props) ? props.open : localOpen;
  const toggle = (): void => {
    if (isControlled(props)) props.onToggle(!open);
    else setLocalOpen((v) => !v);
  };
  const label = props.label ?? "Technical details";
  // Stable id for aria-controls / the panel - React 18+ useId.
  const panelId = useId();

  return (
    <div className={cn("mt-2", props.className)}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        {open ? (
          <ChevronDown className="h-3 w-3" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-3 w-3" aria-hidden="true" />
        )}
        <Wrench className="h-3 w-3" aria-hidden="true" />
        <span>{label}</span>
      </button>
      {open && (
        <div
          id={panelId}
          role="region"
          aria-label={label}
          className="mt-1.5 rounded-md border border-dashed bg-muted/30 p-2"
        >
          {props.children}
        </div>
      )}
    </div>
  );
}

function isControlled(props: TechnicalDetailsProps): props is ControlledProps {
  return props.open !== undefined;
}
